import {
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ContractorListEnum } from '@constants/global.enum';
import { SearchService } from '@services/search.service';
import { VendorService } from '@services/vendor.service';
import { GlobalErrorService } from '@services/global-error.service';
import {
  OpsContractorsListView,
  columnDefs,
} from './ops-contractors-list.config';
import { paginationParams } from '@shared/config/table.config';
import { filterContractorsList } from '@app/shared/modals/ops-contractors-list-filter/ops-contractors-list-filter.helper';
import { AuthService } from '@app/core/auth.service';

@Component({
  selector: 'app-ops-contractors-list',
  templateUrl: './ops-contractors-list.component.html',
  styleUrls: ['./ops-contractors-list.component.scss'],
})
export class OpsContractorsListComponent implements OnInit {
  @ViewChild('customContractorNameColumn', { static: true })
  customContractorNameColumn!: TemplateRef<any>;

  ContractorListEnum = ContractorListEnum;
  columnDefs = columnDefs;
  paginationParams = paginationParams;
  imageSrc = 'assets/img/no-data-found.png';

  contractorsListView: OpsContractorsListView[] = [];
  displayData: OpsContractorsListView[] = [];

  filterValue = '';
  totalCount = 0;
  isDataLoaded = false;
  opened = false;
  defaultEmployeeId: string = '';
  selectedMonth = new Date().getMonth() + 1;
  selectedYear = new Date().getFullYear();
  showContractorProfile = false;
  selectedContractor: OpsContractorsListView | null = null;

  // op-table style client-side sort + pagination (matches the vendor
  // persona open-positions table; emerald-datatable removed)
  sortField: string = 'name';
  sortDir: 'asc' | 'desc' = 'asc';
  pageIndex = 1;
  pageSize = 10;

  constructor(
    private searchService: SearchService,
    private vendorService: VendorService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    public globalError: GlobalErrorService,
    private authService: AuthService,
  ) {}

  async ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      const month = params['month'];
      const year = params['year'];
      if (month && year) {
        this.selectedMonth = month;
        this.selectedYear = year;
        this.fetchContractors(month, year);
      } else {
        this.fetchContractors();
      }
    });
    // Fetch employeeId from AuthService
    const profile = await this.authService.getEmployeeDetails();
    if (profile && profile.employeeId) {
      this.defaultEmployeeId = profile?.employeeId;
    }
  }
  fetchContractors(
    month?: number,
    year?: number,
    additionalFilters?: any,
  ): void {
    this.isDataLoaded = false;
    this.resetData();

    const currentMonth = month || this.selectedMonth;
    const currentYear = year || this.selectedYear;

    this.vendorService
      .getOpsContractorsList(currentMonth, currentYear)
      .subscribe({
        next: (response: any) => {
          if (
            response?.status === ContractorListEnum.Success &&
            Array.isArray(response.data)
          ) {
            this.contractorsListView = response.data.map(this.mapApiData);

            if (additionalFilters) {
              this.displayData = this.applyAdditionalFilters(
                this.contractorsListView,
                additionalFilters,
              );
            } else {
              this.displayData = [...this.contractorsListView];
            }

            this.totalCount = this.displayData.length;
            this.pageIndex = 1;
          } else {
            this.resetData();
          }
          this.isDataLoaded = true;
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.globalError.logComponentError(error);
          this.resetData();
          this.isDataLoaded = true;
          this.cdr.detectChanges();
        },
      });
  }

  searchContractors(event: KeyboardEvent): void {
    const input = (event.target as HTMLInputElement).value.toLowerCase();
    this.filterValue = input;
    this.displayData = this.searchService.getSearchedData(
      this.contractorsListView,
      this.filterValue,
    );
    this.totalCount = this.displayData.length;
    this.pageIndex = 1;
    this.cdr.detectChanges();
  }

  clearInput(): void {
    this.filterValue = '';
    this.displayData = [...this.contractorsListView];
    this.totalCount = this.displayData.length;
    this.pageIndex = 1;
    this.cdr.detectChanges();
  }

  toggleFilter(): void {
    this.opened = !this.opened;
  }

  closeFilter(): void {
    this.opened = false;
  }

  onFilterReset(data: { month: number; year: number }): void {
    this.selectedMonth = data.month;
    this.selectedYear = data.year;

    this.fetchContractors(this.selectedMonth, this.selectedYear);
    this.opened = false;
  }

  onFilterApplied(data: any): void {
    if (data.month) {
      const [selectedMonth, selectedYear] = data.month.split('-');
      this.selectedMonth = Number(selectedMonth);
      this.selectedYear = Number(selectedYear);
      this.fetchContractors(this.selectedMonth, this.selectedYear, data);
    } else if (Array.isArray(data)) {
      this.displayData = data;
      this.filterValue = '';
      this.totalCount = this.displayData.length;
      this.pageIndex = 1;
    }
    this.opened = false;
    this.cdr.detectChanges();
  }

  navigateToOpsVendorDashboard(): void {
    this.router.navigate(['/ops-vendor-management']);
  }

  private resetData(): void {
    this.contractorsListView = [];
    this.displayData = [];
    this.totalCount = 0;
    this.pageIndex = 1;
  }

  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'asc';
    }
    this.pageIndex = 1;
  }

  get sortedData(): any[] {
    const data = [...(this.displayData || [])];
    const f = this.sortField;
    if (!f) return data;
    data.sort((a: any, b: any) => {
      const av = a?.[f];
      const bv = b?.[f];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return this.sortDir === 'asc' ? cmp : -cmp;
    });
    return data;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil((this.displayData?.length || 0) / this.pageSize));
  }

  get pagedData(): any[] {
    const start = (this.pageIndex - 1) * this.pageSize;
    return this.sortedData.slice(start, start + this.pageSize);
  }

  get rangeStart(): number {
    return (this.displayData?.length || 0) === 0 ? 0 : (this.pageIndex - 1) * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.pageIndex * this.pageSize, this.displayData?.length || 0);
  }

  /** Page numbers to render; -1 is an ellipsis gap. */
  get pageWindow(): number[] {
    const total = this.totalPages;
    const cur = this.pageIndex;
    const out: number[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) out.push(i);
      return out;
    }
    out.push(1);
    const start = Math.max(2, cur - 1);
    const end = Math.min(total - 1, cur + 1);
    if (start > 2) out.push(-1);
    for (let i = start; i <= end; i++) out.push(i);
    if (end < total - 1) out.push(-1);
    out.push(total);
    return out;
  }

  goToPage(page: number): void {
    if (page === -1 || page < 1 || page > this.totalPages || page === this.pageIndex) return;
    this.pageIndex = page;
  }

  prevPage(): void {
    this.goToPage(this.pageIndex - 1);
  }

  nextPage(): void {
    this.goToPage(this.pageIndex + 1);
  }

  onPageSizeChange(event: Event): void {
    this.pageSize = +(event.target as HTMLSelectElement).value;
    this.pageIndex = 1;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private mapApiData = (apiData: any): OpsContractorsListView => ({
    name: apiData.contractorName || '',
    reportingManager: apiData.reportingManager || '',
    status: apiData.status || '',
    sowExpiry: apiData.sowExpiryDate || '',
    doj: apiData.dateOfJoining || '',
    location: apiData.location || '',
    totalExp: apiData.totalExperience || '0',
    vendor: apiData.employer || '',
    rto: `${apiData.rtoCount || 0} days`,
    jobTitle: apiData.jobTitle || '',
    employeeId: apiData.employeeId || (this.defaultEmployeeId || ''),
  });

  private applyAdditionalFilters(
    data: OpsContractorsListView[],
    filters: any,
  ): OpsContractorsListView[] {
    return filterContractorsList(data, filters);
  }

  navigateToContractorProfile(data: OpsContractorsListView) {
    this.selectedContractor = data;
    this.showContractorProfile = true;
  }
}
